import * as React from 'react'
import { StyleSheet, View } from 'react-native'
import { useAuthContext } from '../../../contexts/auth-context'
import { StackScreenProps } from '@react-navigation/stack'
import { Text, Button, Card, ActivityIndicator, Chip } from 'react-native-paper'
import {
  useAcceptInvitationMutation,
  useCheckInvitationQuery,
} from '../../../generated/graphql'
import { AppNavigatorParams } from '../../../navigation/types'
import { MessageType } from '../../../utils/message-type'
import Toast from 'react-native-root-toast'
import { useErrorTranslation } from '../../../utils/error-utils'
import { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react-native'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { useTheme } from '../../../hooks/theme.hook'
import { getReturnPath } from '../../../navigation/build-return-path'

export function AcceptInvitationScreen({
  navigation,
  route,
}: StackScreenProps<AppNavigatorParams, 'AcceptInvitation'>) {
  const tError = useErrorTranslation()
  const { t } = useTranslation('invitations')
  const theme = useTheme()
  const { isAnonymous } = useAuthContext()
  const { invitationId, code } = route.params || {}

  // Use whichever parameter was provided (code from URL or invitationId from old links)
  const codeOrId = code || invitationId

  console.log('AcceptInvitation params:', { code, invitationId, codeOrId })
  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (isAnonymous) {
      const returnPath = getReturnPath(navigation)
      navigation.navigate('LoginMobileNumber', { returnPath })
    }
  }, [isAnonymous, navigation])

  const {
    data: invitationCheck,
    loading,
    error: validationError,
  } = useCheckInvitationQuery({
    variables: {
      codeOrId: codeOrId!,
    },
    skip: !codeOrId || isAnonymous,
  })

  const invitation = invitationCheck?.validInvitation.invitation

  useEffect(() => {
    if (!invitationCheck || loading) {
      return
    }
    const { isMember, invitation } = invitationCheck.validInvitation
    if (isMember) {
      Toast.show(t('Already a member'), MessageType.info)
      navigation.replace('ClusterList', {
        nodeId: invitation.nodeId,
      })
    }
  }, [invitationCheck, navigation, loading, t])

  const [accept, { loading: accepting }] = useAcceptInvitationMutation()

  const doAccept = React.useCallback(async () => {
    try {
      const result = await accept({
        variables: {
          codeOrId: codeOrId!,
        },
      })
      if (!result.data) {
        return
      }
      // Navigate to the node after successful acceptance
      navigation.replace('ClusterList', {
        nodeId: invitation?.nodeId,
      })
      Toast.show(t('Invitation accepted'), MessageType.info)
    } catch (e) {
      console.error(e)
      Toast.show(tError(e)!, MessageType.error)
      Sentry.captureException(e)
    }
  }, [accept, codeOrId, invitation, navigation, t])

  return (
    <ScreenWrapper withScrollView={false}>
      <View style={styles.container}>
        {loading && (
          <Card style={styles.card}>
            <Card.Content style={styles.centerContent}>
              <ActivityIndicator size="large" />
              <Text variant="bodyLarge" style={styles.loadingText}>
                {t('Checking invitation...')}
              </Text>
            </Card.Content>
          </Card>
        )}

        {validationError && (
          <Card style={styles.card}>
            <Card.Content style={styles.centerContent}>
              <Text variant="headlineSmall" style={styles.errorText}>
                {validationError.message}
              </Text>
              <Button
                mode="contained"
                onPress={() => navigation.goBack()}
                style={styles.button}
              >
                {t('Ok')}
              </Button>
            </Card.Content>
          </Card>
        )}

        {invitation && !loading && (
          <Card style={styles.card}>
            <Card.Content style={styles.content}>
              <View style={styles.nodeSection}>
                <Text variant="titleLarge" style={styles.nodeTitle}>
                  {t('You are invited to join', {
                    nodeName: invitation.node?.name,
                  })}
                </Text>
              </View>

              {invitation.message && (
                <Card
                  style={styles.messageCard}
                  mode="outlined"
                  theme={{ colors: { outline: theme.colors.primary } }}
                >
                  <Card.Content>
                    <Text variant="bodyLarge" style={styles.message}>
                      {invitation.message}
                    </Text>
                  </Card.Content>
                </Card>
              )}

              <View style={styles.buttons}>
                <Button
                  mode="contained"
                  onPress={doAccept}
                  disabled={accepting}
                  loading={accepting}
                  style={styles.actionButton}
                >
                  {t('Accept')}
                </Button>
                <Button
                  mode="outlined"
                  onPress={() => navigation.goBack()}
                  disabled={accepting}
                  style={styles.actionButton}
                >
                  {t('Ignore')}
                </Button>
              </View>
            </Card.Content>
          </Card>
        )}
      </View>
    </ScreenWrapper>
  )
}

export default AcceptInvitationScreen

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    paddingTop: '20%',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 600,
  },
  centerContent: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 24,
  },
  content: {
    gap: 32,
    paddingVertical: 24,
  },
  nodeSection: {
    alignItems: 'center',
  },
  nodeTitle: {
    textAlign: 'center',
    fontWeight: '700',
  },
  messageCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  message: {
    fontStyle: 'italic',
    lineHeight: 24,
    textAlign: 'center',
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  actionButton: {
    flex: 1,
  },
  button: {
    marginTop: 16,
  },
  loadingText: {
    opacity: 0.7,
  },
  errorText: {
    textAlign: 'center',
    color: '#d32f2f',
  },
})
